import { useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { journeyApi } from '../../api/journeyApi';
import useMemberDisplayNames from '../member-display/useMemberDisplayNames';
import { memberDisplayNameWithId } from '../member-display/memberDisplayModel';
import {
  INVITATION_INTENDED_ROLES,
  buildCreateInvitationBody,
  canRevokeInvitation,
  defaultInvitationExpiresAt,
  invitationErrorMessage,
  isPositiveId,
  normalizeInvitation,
} from './journeyInvitationModel';
import { lifecycleProductLabel } from '../journey-workspace/journeyLifecycleModel';
import {
  patchCreatedInvitation,
  readCreatedInvitations,
  rememberCreatedInvitation,
} from './createdInvitationStore';

export default function JourneyPeopleInviteSection({ journey, disabled = false, onMembersRefresh }) {
  const recipientId = useId();
  const roleId = useId();
  const expiresId = useId();
  const busy = useRef(false);
  const revokeBusy = useRef(false);
  const [open, setOpen] = useState(false);
  const [recipientMemberId, setRecipientMemberId] = useState('');
  const [intendedRole, setIntendedRole] = useState('MEMBER');
  const [expiresAt, setExpiresAt] = useState(() => defaultInvitationExpiresAt());
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [created, setCreated] = useState(() => readCreatedInvitations(journey.journeyId));
  const [confirmRevokeId, setConfirmRevokeId] = useState('');
  const [revokePendingId, setRevokePendingId] = useState('');

  const canInvite = Boolean(journey?.canInvite);
  const manager = journey?.requesterRole === 'OWNER' || journey?.requesterRole === 'COORDINATOR';
  const recipientSummaries = useMemberDisplayNames(
    created.map((invitation) => invitation.recipientMemberId),
  );

  async function submitInvite(event) {
    event.preventDefault();
    if (!canInvite || busy.current || disabled) return;
    if (!isPositiveId(recipientMemberId)) {
      setError('請輸入被邀請者的 memberId。');
      setSuccess('');
      return;
    }
    if (!INVITATION_INTENDED_ROLES.includes(intendedRole)) {
      setError('邀請角色只能是 COORDINATOR 或 MEMBER。');
      return;
    }

    busy.current = true;
    setPending(true);
    setError('');
    setSuccess('');
    try {
      const payload = await journeyApi.createInvitation(
        journey.journeyId,
        buildCreateInvitationBody({ recipientMemberId, intendedRole, expiresAt }),
      );
      const invitation = normalizeInvitation(payload);
      setCreated(rememberCreatedInvitation(journey.journeyId, invitation));
      setSuccess('邀請已建立。對方尚未加入旅程。');
      setRecipientMemberId('');
      if (onMembersRefresh) await onMembersRefresh();
    } catch (cause) {
      setError(invitationErrorMessage(cause));
    } finally {
      busy.current = false;
      setPending(false);
    }
  }

  async function revokeInvitation(invitation) {
    if (!canRevokeInvitation(invitation, { canInvite }) || revokeBusy.current || disabled) return;
    if (confirmRevokeId !== invitation.invitationId) {
      setConfirmRevokeId(invitation.invitationId);
      return;
    }
    revokeBusy.current = true;
    setRevokePendingId(invitation.invitationId);
    setError('');
    try {
      const payload = await journeyApi.revokeInvitation(invitation.invitationId);
      const updated = normalizeInvitation({ ...invitation, ...payload });
      setCreated(patchCreatedInvitation(journey.journeyId, invitation.invitationId, updated));
      setConfirmRevokeId('');
      setSuccess('邀請已撤回。');
    } catch (cause) {
      setError(invitationErrorMessage(cause));
    } finally {
      revokeBusy.current = false;
      setRevokePendingId('');
    }
  }

  return (
    <div className="journey-invite">
      {canInvite ? (
        <div className="journey-invite__actions">
          <button
            type="button"
            className="journey-workspace__lifecycle-primary"
            disabled={disabled || pending}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? '關閉邀請表單' : '邀請旅伴'}
          </button>
        </div>
      ) : manager ? (
        <p className="journey-invite__hint">
          目前旅程狀態為 {lifecycleProductLabel(journey.lifecycleStatus)}，只有規劃中或準備出發可以邀請旅伴。
        </p>
      ) : null}

      {open && canInvite ? (
        <form className="journey-invite__form" onSubmit={submitInvite} noValidate>
          <p className="journey-invite__hint">
            後端只接受 <code>recipientMemberId</code>（memberId），不是 username 或 email。
            成功代表 INVITATION CREATED，不是 MEMBER JOINED。
          </p>
          <label htmlFor={recipientId}>
            被邀請者 memberId
            <input
              id={recipientId}
              inputMode="numeric"
              autoComplete="off"
              value={recipientMemberId}
              disabled={pending || disabled}
              onChange={(event) => setRecipientMemberId(event.target.value)}
            />
          </label>
          <label htmlFor={roleId}>
            邀請角色
            <select
              id={roleId}
              value={intendedRole}
              disabled={pending || disabled}
              onChange={(event) => setIntendedRole(event.target.value)}
            >
              {INVITATION_INTENDED_ROLES.map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </label>
          <label htmlFor={expiresId}>
            expiresAt
            <input
              id={expiresId}
              type="datetime-local"
              value={expiresAt}
              disabled={pending || disabled}
              onChange={(event) => setExpiresAt(event.target.value)}
            />
          </label>
          <button
            type="submit"
            className="journey-workspace__lifecycle-primary"
            disabled={pending || disabled}
          >
            {pending ? '送出中…' : '送出邀請'}
          </button>
        </form>
      ) : null}

      {error ? <p className="journey-workspace__error" role="alert">{error}</p> : null}
      {success ? <p className="journey-invite__success" role="status">{success}</p> : null}

      {created.length ? (
        <section className="journey-invite__created" aria-labelledby="created-invites-heading">
          <h3 id="created-invites-heading">已建立的邀請</h3>
          <p className="journey-invite__hint">
            對方可在「旅程邀請」看到這張邀請。這裡只顯示本裝置剛建立的紀錄，尚未有 outgoing server list。
          </p>
          <ul>
            {created.map((invitation) => (
              <li key={invitation.invitationId}>
                <p className="journey-invite__id">invitationId {invitation.invitationId}</p>
                <p className="journey-invite__recipient">
                  {memberDisplayNameWithId(invitation.recipientMemberId, recipientSummaries)}
                </p>
                <p>
                  {invitation.intendedRole}
                  {' · '}
                  <span>{invitation.statusLabel} ({invitation.status})</span>
                </p>
                {invitation.expiresAt ? <p>expiresAt {invitation.expiresAt}</p> : null}
                <div className="journey-invite__row-actions">
                  <Link to={`/invitations?invitationId=${invitation.invitationId}`}>邀請入口</Link>
                  {canRevokeInvitation(invitation, { canInvite }) ? (
                    <button
                      type="button"
                      className="journey-workspace__lifecycle-danger"
                      disabled={disabled || revokePendingId === invitation.invitationId}
                      onClick={() => revokeInvitation(invitation)}
                    >
                      {revokePendingId === invitation.invitationId
                        ? '撤回中…'
                        : confirmRevokeId === invitation.invitationId
                          ? '確認撤回邀請'
                          : '撤回邀請'}
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
